/** @format */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Chip, CircularProgress, Tooltip } from "@mui/material";
import {
    AutoMode,
    PauseCircleOutline,
    CheckCircle,
    WarningAmber,
    Visibility,
    Engineering,
    Handshake,
    EventAvailable,
    Refresh,
    ErrorOutline,
} from "@mui/icons-material";

import { CollectSectionCard } from "../styles";
import { colors } from "../../../utility/types/constant";
import { IAutopilotSummary } from "../../../utility/types/collectTypes";
import { submitGetAutopilotSummary } from "../../../redux/services/collectService";

const FONT = "'Montserrat', sans-serif";

interface Props {
    businessId: string | null;
    /** Optional external refresh ping — bump to refetch. */
    refreshKey?: number;
}

function timeAgo(value?: string | null): string {
    if (!value) return "—";
    const diff = Date.now() - new Date(value).getTime();
    if (diff < 0) return "just now";
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

interface StatTileProps {
    icon: React.ReactNode;
    label: string;
    value: number;
    color: string;
    onClick?: () => void;
    emphasize?: boolean;
}

const StatTile: React.FC<StatTileProps> = ({
    icon,
    label,
    value,
    color,
    onClick,
    emphasize,
}) => (
    <Box
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        sx={{
            flex: "1 1 120px",
            minWidth: 120,
            border: `1px solid ${emphasize ? color : "#e5e7eb"}`,
            backgroundColor: emphasize ? `${color}0d` : "#fff",
            borderRadius: "12px",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            cursor: onClick ? "pointer" : "default",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": onClick
                ? { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0,0,0,0.08)" }
                : {},
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color }}>
            {icon}
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontSize: "1.35rem",
                    fontWeight: 800,
                    color: "#111827",
                    lineHeight: 1,
                }}
            >
                {value}
            </Typography>
        </Box>
        <Typography
            sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}
        >
            {label}
        </Typography>
    </Box>
);

const AutopilotSummaryPanel: React.FC<Props> = ({ businessId, refreshKey }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<IAutopilotSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const requestSequence = useRef(0);

    const load = useCallback(async () => {
        const requestId = ++requestSequence.current;
        if (!businessId) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        setData(null);
        try {
            const res = await submitGetAutopilotSummary(businessId);
            if (requestId === requestSequence.current) setData(res);
        } catch (e: any) {
            if (requestId === requestSequence.current) {
                setError(e?.message || "Failed to load autopilot status.");
            }
        } finally {
            if (requestId === requestSequence.current) setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        load();
        return () => {
            requestSequence.current += 1;
        };
    }, [load, refreshKey]);

    if (!businessId) return null;

    const ap = data?.autopilot;
    const isOn = Boolean(ap?.enabled);
    const isApproval = ap?.mode === "approval";

    const statusColor = !data
        ? "#9ca3af"
        : !isOn
            ? "#b91c1c"
            : isApproval
                ? "#b45309"
                : "#15803d";

    const statusLabel = !data
        ? "Checking…"
        : !isOn
            ? "Paused"
            : isApproval
                ? "Approval mode"
                : "On";

    const StatusIcon = !isOn ? PauseCircleOutline : isApproval ? CheckCircle : AutoMode;

    const attention = data?.portfolio.attentionCount ?? 0;
    const working = data?.portfolio.workingCount ?? 0;
    const deliveryIssues = data?.portfolio.deliveryIssues ?? 0;

    // Build the plain-English reassurance line.
    const headline = (() => {
        if (!data) return "Checking what's running for you…";
        if (!isOn) {
            return ap?.reason
                ? `Automation is paused — ${ap.reason}.`
                : "Automation is paused.";
        }
        if (working === 0 && attention === 0) {
            return "You're all caught up, `no overdue invoices need chasing right now.";
        }
        const parts: string[] = [];
        parts.push(
            `${working} invoice${working === 1 ? "" : "s"} being worked`,
        );
        if (attention > 0) {
            parts.push(
                `${attention} need${attention === 1 ? "s" : ""} your attention`,
            );
        } else {
            parts.push("nothing needs you right now");
        }
        const base = isApproval
            ? "Drafts are prepared and waiting for your approval"
            : "We're chasing your overdue invoices automatically";
        return `${base} — ${parts.join(", ")}.`;
    })();

    const bd = data?.attentionBreakdown;
    const attentionChips: Array<{ label: string; n: number }> = bd
        ? [
            { label: "Manual review", n: bd.manualReview },
            { label: "Disputes", n: bd.disputes },
            { label: "Broken promises", n: bd.brokenPromises },
            { label: "Verify payment", n: bd.verifyPayment },
            { label: "Reply review", n: bd.replyReview },
            { label: "Other", n: bd.other },
        ].filter((c) => c.n > 0)
        : [];

    return (
        <CollectSectionCard>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AutoMode sx={{ color: colors.primary }} />
                    <Typography
                        sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.05rem", color: "#111827" }}
                    >
                        Autopilot
                    </Typography>
                    <Chip
                        icon={<StatusIcon sx={{ fontSize: "1rem !important", color: `${statusColor} !important` }} />}
                        label={statusLabel}
                        size="small"
                        sx={{
                            backgroundColor: `${statusColor}1a`,
                            color: statusColor,
                            fontFamily: FONT,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                        }}
                    />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {data?.lastAction && (
                        <Tooltip title={data.lastAction.title} arrow>
                            <Typography
                                sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#6b7280" }}
                            >
                                Last action {timeAgo(data.lastAction.at)}
                            </Typography>
                        </Tooltip>
                    )}
                    <Tooltip title="Refresh" arrow>
                        <Refresh
                            onClick={load}
                            sx={{
                                fontSize: "1.1rem",
                                color: "#9ca3af",
                                cursor: "pointer",
                                "&:hover": { color: colors.primary },
                            }}
                        />
                    </Tooltip>
                </Box>
            </Box>

            {/* Headline reassurance line */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.25,
                    borderRadius: "10px",
                    backgroundColor: !isOn ? "#fef2f2" : isApproval ? "#fffbeb" : "#f0fdf4",
                    border: `1px solid ${!isOn ? "#fecaca" : isApproval ? "#fde68a" : "#bbf7d0"}`,
                    mb: 1.75,
                }}
            >
                {loading && !data ? (
                    <CircularProgress size={16} sx={{ color: colors.primary }} />
                ) : (
                    <StatusIcon sx={{ fontSize: "1.25rem", color: statusColor }} />
                )}
                <Typography
                    sx={{ fontFamily: FONT, fontSize: "0.86rem", color: "#374151", fontWeight: 600 }}
                >
                    {error || headline}
                </Typography>
            </Box>

            {/* Stat tiles */}
            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mb: attentionChips.length || deliveryIssues ? 1.5 : 0 }}>
                <StatTile
                    icon={<Engineering sx={{ fontSize: "1.1rem" }} />}
                    label="Being worked"
                    value={working}
                    color={colors.primary}
                />
                <StatTile
                    icon={<Visibility sx={{ fontSize: "1.1rem" }} />}
                    label="Monitoring"
                    value={data?.portfolio.monitoringCount ?? 0}
                    color="#0e7490"
                />
                <StatTile
                    icon={<WarningAmber sx={{ fontSize: "1.1rem" }} />}
                    label="Needs you"
                    value={attention}
                    color="#b45309"
                    emphasize={attention > 0}
                    onClick={() => navigate("/collect/tasks")}
                />
                {deliveryIssues > 0 && (
                    <StatTile
                        icon={<ErrorOutline sx={{ fontSize: "1.1rem" }} />}
                        label="Delivery issues"
                        value={deliveryIssues}
                        color="#b91c1c"
                        emphasize
                    />
                )}
            </Box>

            {/* Attention breakdown chips */}
            {attentionChips.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                    {attentionChips.map((c) => (
                        <Chip
                            key={c.label}
                            label={`${c.label}: ${c.n}`}
                            size="small"
                            onClick={() => navigate("/collect/tasks")}
                            sx={{
                                fontFamily: FONT,
                                fontSize: "0.68rem",
                                fontWeight: 600,
                                backgroundColor: "#fff7ed",
                                color: "#9a3412",
                                border: "1px solid #fed7aa",
                                cursor: "pointer",
                            }}
                        />
                    ))}
                </Box>
            )}

            {/* Next 7 days */}
            {data && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                        pt: 1.25,
                        borderTop: "1px solid #f1f5f9",
                    }}
                >
                    <Typography
                        sx={{ fontFamily: FONT, fontSize: "0.72rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}
                    >
                        Next 7 days
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <Handshake sx={{ fontSize: "1rem", color: "#15803d" }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: "0.8rem", color: "#374151", fontWeight: 600 }}>
                            {data.next7Days.promisesDue} promise{data.next7Days.promisesDue === 1 ? "" : "s"} due
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <EventAvailable sx={{ fontSize: "1rem", color: "#0e7490" }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: "0.8rem", color: "#374151", fontWeight: 600 }}>
                            {data.next7Days.invoicesComingDue} invoice{data.next7Days.invoicesComingDue === 1 ? "" : "s"} coming due
                        </Typography>
                    </Box>
                    {!ap?.quickBooksConnected && (
                        <Chip
                            label="QuickBooks not connected"
                            size="small"
                            sx={{
                                fontFamily: FONT,
                                fontSize: "0.66rem",
                                fontWeight: 700,
                                backgroundColor: "#fef2f2",
                                color: "#b91c1c",
                                border: "1px solid #fecaca",
                            }}
                        />
                    )}
                </Box>
            )}
        </CollectSectionCard>
    );
};

export default AutopilotSummaryPanel;
